import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import { Dictionary } from 'lodash';
import { localeContext } from 'helpers/Locale/LocaleManager';
import { applicationContext } from 'helpers/services';

interface ITicketCategoryDetailsContext {
    isLoading: boolean
    ticketCategory: TicketCategory
    categoryName: Dictionary<string>
    subCategories: SubCategory[]

    saveTicketCategory: (values: TicketCategory) => void
    updateName: (newValue: string, locale: string) => void
    searchUsers: (searchText: string) => void
}

export let subCategorySchema = yup.object().shape({
    id: yup.string().default(''),
    defaultGroups: yup.array(yup.string()).default([]),
    defaultNotification: yup.array(yup.object({ key: yup.string(), value: yup.string() })).default([]),
    italianName: yup.string().default(''),
    englishName: yup.string().default(''),
    canBeSelectedByCustomer: yup.boolean().default(true),
    isNew: yup.boolean().default(true),
    galleryCategory: yup.string().default('').nullable(),
    galleryTypology: yup.string().default('').nullable(),
});

export let ticketCategorySchema = yup.object().shape({
    ticketCategoryId: yup.string().default(''),
    color: yup.string().default(''),
    fromCustomer: yup.boolean().nullable().default(false),
    subCategories: yup.array(subCategorySchema).default([])
});

export type TicketCategory = yup.InferType<typeof ticketCategorySchema>;
export type SubCategory = yup.InferType<typeof subCategorySchema>;

export interface ISubCategory {
    code: string
    names: Dictionary<string>
    defaultNotification: string[]
}


export let ticketCategoryDetailsContext = React.createContext({} as ITicketCategoryDetailsContext);

let { Provider } = ticketCategoryDetailsContext;

let TicketCategoryDetailsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id } = useParams<{ id: string }>();
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();
    let router = useLocation();

    let localeService = useContext(localeContext);
    let appService = useContext(applicationContext);

    let [isLoading, setLoading] = useState(false);
    let [ticketCategory, setTicketCategory] = useState<TicketCategory>(ticketCategorySchema.getDefault());
    let [categoryName, setCategoryName] = useState<Dictionary<string>>({} as Dictionary<string>);
    let [subCategories, setSubCategories] = useState<SubCategory[]>([]);

    let isNew = router.pathname.includes('/addnew');

    useEffect(() => {
        localeService.locales
            .forEach(locale => {
                if (!categoryName[locale.key])
                    categoryName[locale.key] = '';
            });

        setCategoryName(categoryName);

        if (isNew === false)
            getTicketCategory();
    }, []);

    let saveTicketCategory = async (values: TicketCategory) => {
        try {
            setLoading(true);

            let request = {
                ...values,
                name: categoryName
            }

            let res = await axios.post('/api/TicketCategory/SaveTicketCategory', request);

            if (res.data.affectedRecords === 1) {
                await appService.getSimecReferenceData();

                enqueueSnackbar("Categoria ticket salvata", { variant: "success" });
                history.push("/ticketcategories");
            }

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let updateName = (newValue: string, locale: string) => {
        setCategoryName({
            ...categoryName,
            [locale]: newValue
        });
    }

    let getTicketCategory = async () => {
        try {
            setLoading(true);

            let res = await axios.get('/api/TicketCategory/GetTicketCategory', { params: { ticketCategoryId: id } });

            setTicketCategory(res.data.item);

            setCategoryName(res.data.item.name);
            setSubCategories(res.data.item.subCategories);

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }


    let searchUsers = async (searchText: string) => {
        if (!searchText)
            return;

        try {
            let request = {
                searchText: searchText
            };

            const res = await axios.get('/api/User/GetTicketUsers', { params: request });
            return res.data.items;
        } catch { }
    }

    return (
        <Provider value={{
            isLoading,
            ticketCategory,
            categoryName,
            subCategories,

            saveTicketCategory,
            updateName,
            searchUsers
        }}>
            {children}
        </Provider>
    );
}

export default TicketCategoryDetailsProvider;